import { Fragment, useLayoutEffect, type ReactElement } from "react";
import { logical } from "../../ui/kit/index.ts";
import { AlertMark } from "./log.tsx";
import type { ChatSuggestion, ListKind } from "./stepper.ts";
import { say } from "../../words.ts";

/**
 * The stepper's list over the chat log (ADR-068's amendment "chat commands, Discord style", item 2): the commands a
 * slash and a partial word start, or the names, families or recent whisperers an option offers, the highlighted one
 * marked. It is a listbox the input owns: the input keeps the focus, names the listbox and the highlighted option
 * (`aria-controls`, `aria-activedescendant`), and Up and Down move the highlight, so assistive technology reads each
 * entry as it is reached. A press on an entry keeps the input's focus and a click chooses it. While the account's
 * lookups wait, the list says so in an error line with the alert mark (R-0027) and still offers the names in view.
 */

/**
 * The listbox's id, which the input names.
 */
export const STEPS_ID = "ot-chat-steps";

/**
 * An entry's id, which the input names while it is highlighted.
 *
 * @param index The entry's place.
 * @returns The id.
 */
export function entryId(index: number): string {
  return `${STEPS_ID}-${index}`;
}

/**
 * The list.
 *
 * @param props The list's `kind` and `entries`, the `highlight`ed entry, what choosing one does (`onChoose`), the
 *   seconds the account's lookups still wait (`waitSeconds`, 0 for none), and the list's `bottom` in logical px
 *   above the panel's body's foot, so it sits over the log.
 * @returns The list, or nothing when it has neither entries nor a wait to show.
 */
export function StepsList({ kind, entries, highlight, onChoose, waitSeconds, bottom }: { readonly kind: ListKind | null; readonly entries: readonly ChatSuggestion[]; readonly highlight: number; readonly onChoose: (index: number) => void; readonly waitSeconds: number; readonly bottom: number }): ReactElement | null {
  useLayoutEffect(() => {
    document.getElementById(entryId(highlight))?.scrollIntoView?.({ block: "nearest" });
  }, [highlight, entries]);
  if (entries.length === 0 && waitSeconds === 0) {
    return null;
  }
  return (
    <div className="ot-chat__steps ot-ui-frame-panel" data-chat="steps" style={{ bottom: logical(bottom) }} onMouseDown={(event) => event.preventDefault()}>
      {entries.length === 0 ? null : (
        <ul className="ot-chat__options" id={STEPS_ID} role="listbox" aria-label={kind === "commands" ? say("chat.commandsList") : say("chat.namesList")} data-chat="options" data-chat-list={kind ?? ""}>
          {entries.map((entry, index) => (
            <Fragment key={entry.id}>
              {entry.heading === undefined ? null : (
                <li className="ot-chat__option-group" role="presentation" data-chat="option-group">
                  {entry.heading}
                </li>
              )}
              <li id={entryId(index)} className="ot-chat__option" role="option" aria-selected={index === highlight} data-chat="option" onClick={() => onChoose(index)}>
                <span className="ot-chat__option-label">{entry.label}</span>
                {entry.detail === "" ? null : <span className="ot-chat__option-detail">{entry.detail}</span>}
                {entry.description === "" ? null : <span className="ot-chat__option-description">{entry.description}</span>}
              </li>
            </Fragment>
          ))}
        </ul>
      )}
      {waitSeconds === 0 ? null : (
        <p className="ot-chat__line ot-chat__line--error ot-chat__wait" data-chat="lookup-wait">
          <AlertMark />
          {say("chat.lookupWait", { seconds: String(waitSeconds) })}
        </p>
      )}
    </div>
  );
}
